import {defineStore} from 'pinia';
import {DECLUTTER_MAX_TIME_INTERVAL} from '~/constants/declutter.js';
import {getFilesWithDeclutterGroupData} from '~/utils/file.js';
import {useFileApi} from '~/composables/file/api.js';
import {FILE_UPLOAD_SOURCES} from '~/constants/files.js';
import {useStorage} from '@vueuse/core';
import {FILE_FILTER_PANEL_STATE_STORAGE_KEY} from '~/constants/files.js';
import {normalizeRotation} from '~/utils/math.js';
import {normalizeFileCollectionDescriptor} from '~/utils/file-filter-policy.js';

const BUFFER_PAGES_COUNT = 1;
const dayjs = useDayjs();

// Shared by file pages, detail responses, and detail-request ID batches.
function filterExcludedTimelineItems(items, excludedIds) {
  return items.filter(item => !excludedIds.has(String(item?.id ?? item)));
}

function getEffectiveDateRange(scopeDateRange, dateRange) {
  const scopeValues = scopeDateRange?.val;
  const filterValues = dateRange?.val;

  if (!scopeValues) {
    return dateRange;
  }

  if (!filterValues) {
    return scopeDateRange;
  }

  const start = dayjs(scopeValues[0]).isAfter(dayjs(filterValues[0]))
    ? scopeValues[0]
    : filterValues[0];
  const end = dayjs(scopeValues[1]).isBefore(dayjs(filterValues[1]))
    ? scopeValues[1]
    : filterValues[1];

  return {
    val: [start, end]
  };
}

function getEffectiveFileTypes(scopeFileTypes, fileTypes) {
  if (!scopeFileTypes?.length) {
    return {values: fileTypes};
  }

  if (!fileTypes?.length) {
    return {values: scopeFileTypes};
  }

  const values = fileTypes.filter(fileType => scopeFileTypes.includes(fileType));

  return {
    values,
    hasNoMatches: values.length === 0
  };
}

export const makeFilesStore = (id = 'filesStore') => defineStore(id, {
  state: () => ({
    files: null,
    filesCount: null,
    bufferedPages: null,
    hasMorePages: false,
    hasMore: false,
    cursor: null,
    isBuffering: false,
    isPageQueued: false,
    collectionDescriptor: null,
    isLoadingFiles: false,
    excludedTimelineFileIds: new Set(),

    fileDetails: null,
    fileDetailRequestTracker: new Set(),
    file: null
  }),

  actions: {
    /**
     * when reload is true all of the parameters will be preserved but files will be cleared and loading will start again from page 1
     */
    async getFiles({
      nextPage,
      reload,
      userId,
      order,
      albums = [],
      tags = [],
      people = [],
      fileTypes = [],
      organization = [],
      uploadSourceNames,
      dateRange = {},
      scopeDateRange,
      scopeFileTypes,
      addedSince,
      search,
      contextType,
      context,
      subcontext,
      shareToken,
      projectsOnly,
      projectsCategory,
      storeCollection = false,
      useStoredCollection = false,
      updateContextFileOrder = true
    } = {}) {

      const resetTracking = () => {
        this.cursor = null;
        this.filesCount = null;
        this.bufferedPages = [];
        this.hasMore = false;
        this.excludedTimelineFileIds = new Set();
      };

      if (!nextPage) {
        resetTracking();

        if (!reload) {
          if (useStoredCollection) {
            this.collectionDescriptor = JSON.parse(useStorage(FILE_FILTER_PANEL_STATE_STORAGE_KEY, null, sessionStorage).value);
          }

          if (!useStoredCollection || !this.collectionDescriptor) {
            this.collectionDescriptor = {
              userId,
              order,
              albums,
              tags,
              people,
              fileTypes,
              organization,
              uploadSourceNames,
              addedSince,
              search,
              contextType,
              context,
              subcontext,
              shareToken,
              dateRange,
              scopeDateRange,
              scopeFileTypes,
              projectsOnly,
              projectsCategory,
              matchesLoadedFiles: false
            };
          }

          if (updateContextFileOrder && context?.isOwnedByUser && context.file_sort !== order.paramName) {
            const sortPersistArgs = {id: context.id, updates: {file_sort: order.paramName}};

            if (contextType === FILE_CONTEXTS.album) {
              useAlbumsStore().updateAlbum(sortPersistArgs);
            }
            if (contextType === FILE_CONTEXTS.tag) {
              useTagsStore().updateTag(sortPersistArgs);
            }
            if (contextType === FILE_CONTEXTS.person) {
              usePeopleStore().updatePerson(sortPersistArgs);
            }
          }
        }

        this.collectionDescriptor = normalizeFileCollectionDescriptor(this.collectionDescriptor);

        this.getFilesCount();
      }

      if (storeCollection) {
        useStorage(FILE_FILTER_PANEL_STATE_STORAGE_KEY, {}, sessionStorage).value = this.collectionDescriptor;
      }

      const fetchPage = async () => {
        const effectiveDateRange = this.collectionDescriptor.contextType === FILE_CONTEXTS.timeline
          ? null
          : getEffectiveDateRange(
            this.collectionDescriptor.scopeDateRange,
            this.collectionDescriptor.dateRange
          );
        const effectiveFileTypes = getEffectiveFileTypes(
          this.collectionDescriptor.scopeFileTypes,
          this.collectionDescriptor.fileTypes
        );
        const data = this.collectionDescriptor.projectsOnly && this.collectionDescriptor.contextType !== FILE_CONTEXTS.timeline
          ? await useProjectsApi().getProjects({
            cursor: this.cursor,
            search: this.collectionDescriptor.search,
            filter: this.collectionDescriptor.projectsCategory
          })
          : effectiveFileTypes.hasNoMatches
            ? {files: [], per_page: 1, next_cursor: null}
            : await useFileApi().getFiles({
              cursor: this.cursor,
              userId: this.collectionDescriptor.userId,
              order: this.collectionDescriptor.order,
              albumIds: this.collectionDescriptor.albums?.map(a => a.id),
              tagIds: this.collectionDescriptor.tags?.map(t => t.id),
              personIds: this.collectionDescriptor.people?.map(p => p.id),
              fileTypes: effectiveFileTypes.values,
              organization: this.collectionDescriptor.organization,
              uploadSourceNames: this.collectionDescriptor.uploadSourceNames,
              startDate: effectiveDateRange?.val && effectiveDateRange.val[0],
              endDate: effectiveDateRange?.val && effectiveDateRange.val[1],
              addedSince: this.collectionDescriptor.addedSince,
              search: this.collectionDescriptor.search,
              contextType: this.collectionDescriptor.contextType,
              contextId: this.collectionDescriptor.context?.id,
              subcontext: this.collectionDescriptor.subcontext,
              shareToken: this.collectionDescriptor.shareToken
            });

        this.hasMorePages = data.files.length === data.per_page;
        this.cursor = data.next_cursor;

        return data;
      };
      const bufferPages = async () => {
        if (this.isBuffering) {
          return;
        }

        this.isBuffering = true;
        await nextTick();

        while (this.hasMorePages && this.bufferedPages?.length < BUFFER_PAGES_COUNT) {
          const pageData = await fetchPage();
          this.bufferedPages?.push(pageData);

          if (this.isPageQueued) {
            processPage();
          }
        }

        this.isBuffering = false;
      };
      const processPage = ({page, resetFiles} = {}) => {
        this.isPageQueued = false;

        const thePage = page || this.bufferedPages?.shift();

        if (this.hasMore && !thePage) {
          //note: there are more pages, but the buffer is empty - queue up a page to process when the buffer gets something
          this.isPageQueued = true;
          return;
        }

        //note: modify this to apply file-mapper if/when deemed necessary
        // A page fetched before a date edit can still contain a file that left the year.
        const pageFiles = this.collectionDescriptor.contextType === FILE_CONTEXTS.timeline
          ? filterExcludedTimelineItems(thePage.files, this.excludedTimelineFileIds)
          : thePage.files;
        const groupedFiles = this.addFileGroupData(pageFiles);

        if (resetFiles) {
          this.files = [];
          this.collectionDescriptor.matchesLoadedFiles = true;
        }

        this.files = [...this.files, ...groupedFiles];
        this.hasMore = Boolean(this.bufferedPages.length || this.hasMorePages);
      }

      try {
        this.files = this.files || [];
        this.bufferedPages = this.bufferedPages || [];
        this.isLoadingFiles = true;

        const page = nextPage
          ? null
          : await fetchPage();

        processPage({page, resetFiles: !nextPage || reload});
      } catch (e) {
        throw e;
      } finally {
        this.isLoadingFiles = false;
      }

      if (process.client) {
        bufferPages();
      }

      return this.files;
    },

    /**
     * NOTE: This action is for special use only with the declutter component. This store needs to be running in a separate instance than the default for this to be used. It is mutually exclusive with the getFiles action.
     */
    async getFilesForDeclutter({nextPage, context, contextType, subcontext, dateRange, scopeDateRange} = {}) {
      const startFilesCount = this.files?.length || 0;

      if (!nextPage) {
        this.files = [];
        this.cursor = null;
        this.hasMore = false;
        this.collectionDescriptor = {
          order: FILE_SORT_OPTIONS[0],
          context,
          contextType,
          subcontext,
          dateRange,
          scopeDateRange,
          fileTypes: [FILE_TYPES.photo],
          takenAtDateType: 'exact_date_and_time', //todo: use constant after merging with fuzzy dates branch
          uploadSourceNames: [
            FILE_UPLOAD_SOURCES.unknown,
            FILE_UPLOAD_SOURCES.android,
            FILE_UPLOAD_SOURCES.artisan,
            FILE_UPLOAD_SOURCES.facebook,
            FILE_UPLOAD_SOURCES.historian,
            FILE_UPLOAD_SOURCES.ios,
            FILE_UPLOAD_SOURCES.jupiter,
            FILE_UPLOAD_SOURCES.valet,
            FILE_UPLOAD_SOURCES.web
          ]
        };
        this.collectionDescriptor = normalizeFileCollectionDescriptor(this.collectionDescriptor);
      }

      const effectiveDateRange = this.collectionDescriptor.contextType === FILE_CONTEXTS.timeline
        ? null
        : getEffectiveDateRange(
          this.collectionDescriptor.scopeDateRange,
          this.collectionDescriptor.dateRange
        );
      const data = await useFileApi().getFiles({
        cursor: this.cursor,
        order: this.collectionDescriptor.order,
        contextId: this.collectionDescriptor.context?.id,
        contextType: this.collectionDescriptor.contextType,
        subcontext: this.collectionDescriptor.subcontext,
        fileTypes: this.collectionDescriptor.fileTypes,
        takenAtDateType: this.collectionDescriptor.takenAtDateType,
        startDate: effectiveDateRange?.val && effectiveDateRange.val[0],
        endDate: effectiveDateRange?.val && effectiveDateRange.val[1],
        uploadSourceNames: this.collectionDescriptor.uploadSourceNames
      });

      this.hasMore = data.files.length === data.per_page;
      this.cursor = data.next_cursor;

      const files = data.files;
      const groupedFiles = getFilesWithDeclutterGroupData({files, interval: DECLUTTER_MAX_TIME_INTERVAL});

      this.files = [...this.files, ...groupedFiles];

      //if the received page produced no files for declutter get the next page automatically
      if (this.files.length === startFilesCount && this.hasMore) {
        await this.getFilesForDeclutter({nextPage: true});
      }

      return this.files;
    },

    async getFilesCount() {
      if (this.collectionDescriptor) {
        /*this.filesCount = await useFileApi().getFilesCount({
          userId: this.collectionDescriptor.userId,
          order: this.collectionDescriptor.order,
          albumIds: this.collectionDescriptor.albums?.map(a => a.id),
          tagIds: this.collectionDescriptor.tags?.map(t => t.id),
          personIds: this.collectionDescriptor.people?.map(p => p.id),
          fileTypes: this.collectionDescriptor.fileTypes,
          organization: this.collectionDescriptor.organization,
          startDate: this.collectionDescriptor.dateRange?.val && this.collectionDescriptor.dateRange.val[0],
          endDate: this.collectionDescriptor.dateRange?.val && this.collectionDescriptor.dateRange.val[1],
          addedSince: this.collectionDescriptor.addedSince,
          search: this.collectionDescriptor.search,
          contextType: this.collectionDescriptor.contextType,
          contextId: this.collectionDescriptor.context?.id,
          shareToken: this.collectionDescriptor.shareToken
        });*/
      }
    },

    clearFiles({preserveDetailFile} = {}) {
      this.files = null;
      this.filesCount = null;
      this.bufferedPages = null;
      this.hasMore = false;
      this.cursor = null;
      this.collectionDescriptor = null;
      this.fileDetails = null;
      this.fileDetailRequestTracker = new Set();
      this.excludedTimelineFileIds = new Set();

      if (!preserveDetailFile) {
        this.file = null;
      }
    },

    async clearFilters({storeCollection}) {

      this.collectionDescriptor.albums = [];
      this.collectionDescriptor.tags = [];
      this.collectionDescriptor.people = [];
      this.collectionDescriptor.fileTypes = [];
      this.collectionDescriptor.organization = [];
      this.collectionDescriptor.dateRange = null;
      await this.getFiles({reload: true, storeCollection});
    },

    prependFiles({files}) {
      this.files.unshift(...files);
    },

    async getFileDetails({files, userId, shareToken, contextType, contextId, subcontext, refresh}) {
      const requests = [];
      const requestCollection = this.collectionDescriptor;
      const isCurrentTimelineCollection = () => contextType === FILE_CONTEXTS.timeline
        && requestCollection === this.collectionDescriptor
        && this.isSameAsLoadedFilesContext({contextType, contextId, subcontext});
      const excludeRemoved = items => isCurrentTimelineCollection()
        ? filterExcludedTimelineItems(items, this.excludedTimelineFileIds)
        : items;
      const eligibleFiles = excludeRemoved(files);
      const filesNeedDetails = refresh
        ? eligibleFiles
        : eligibleFiles.filter(f => (!f.is?.detail || f.is.processing) && !this.fileDetailRequestTracker.has(f.id));

      const fileApi = useFileApi();
      const requestContext = {
        contextId,
        contextType: [FILE_CONTEXTS.gallery, FILE_CONTEXTS.file].includes(contextType) ? null : contextType,
        subcontext,
        userId,
        shareToken
      };
      async function fetchBatchDetails(ids) {
        try {
          return await fileApi.getFileDetails(ids, requestContext);
        } catch (error) {
          const status = Number(error?.statusCode || error?.status || error?.response?.status);
          if (!isCurrentTimelineCollection() || status !== 400
            || error?.data?.errors !== 'not all ids belong to context') throw error;

          const remainingIds = excludeRemoved(ids);
          if (remainingIds.length === ids.length) throw error;
          // The date edit can complete while a staged-detail batch is in flight.
          // Retry only confirmed remaining members, once; unrelated failures still surface.
          return remainingIds.length ? fileApi.getFileDetails(remainingIds, requestContext) : [];
        }
      }

      for (let i = 0; i < filesNeedDetails.length; i += 20) {
        const batchPromise = new Promise(async (resolve, reject) => {
          const fileIdsForRequest = filesNeedDetails.slice(i, i + 20).map(f => f.id);

          fileIdsForRequest.forEach(fId => this.fileDetailRequestTracker.add(fId));

          try {
            const batchFileDetails = await fetchBatchDetails(fileIdsForRequest);
            const visibleDetails = excludeRemoved(batchFileDetails);
            const detailedRecords = this.addFileGroupData(visibleDetails);

            this.fileDetails = this.fileDetails || [];
            this.fileDetails.push(...detailedRecords);

            //merge with files
            for (let i = 0; i < this.fileDetails.length; i++) {
              // this.fileDetailRequestTracker.delete(this.fileDetails[i].id);

              const fd = this.fileDetails[i];

              //set loaded file
              if (this.file?.id === fd.id) {
                this.file = fd;
              }

              //merge with list files
              if (this.files && (!contextId || this.isSameAsLoadedFilesContext({contextType, contextId, subcontext}))) {
                const fileIndex = this.files.findIndex(f => f.id === fd.id);

                if (fileIndex > -1) {
                  this.files[fileIndex] = fd;// Object.assign(this.files[fileIndex], fd);
                  this.fileDetails.splice(i, 1);
                  i--;
                }
              }
            }

            resolve(detailedRecords);

          } catch (e) {
            reject(e);
          } finally {
            fileIdsForRequest.forEach(fId => this.fileDetailRequestTracker.delete(fId));
          }
        });

        requests.push(batchPromise);
      }

      const detailedRecordsArr = await Promise.all(requests);

      return detailedRecordsArr.reduce((accum, arr) => accum.concat(arr), []);
    },

    setDigitizationFiles({files}) {
      const currentDetailFileId = this.file?.id;
      this.clearFiles();
      this.files = files;
      this.collectionDescriptor = {
        contextType: FILE_CONTEXTS.digitization
      };
      this.collectionDescriptor.matchesLoadedFiles = true;

      const detailFile = currentDetailFileId && this.files.find(f => f.id === currentDetailFileId);
      if (detailFile) {
        this.setFile({file: detailFile});
      }
    },

    isSameAsLoadedFilesContext({contextType, contextId, subcontext}) {
      const loadedSubcontext = this.collectionDescriptor?.subcontext;
      const isMatchingSubcontext = loadedSubcontext?.type === subcontext?.type
        && loadedSubcontext?.id === subcontext?.id;
      const isMatchingFileContext = (this.collectionDescriptor?.contextType === contextType && this.collectionDescriptor?.context?.id === contextId)
        && (contextType !== FILE_CONTEXTS.timeline || isMatchingSubcontext)
        || (contextType === FILE_CONTEXTS.project && this.collectionDescriptor?.projectsOnly)
        || [FILE_CONTEXTS.gallery, FILE_CONTEXTS.digitization].includes(contextType);


      return isMatchingFileContext && this.collectionDescriptor?.matchesLoadedFiles;
    },

    insertFile({file, afterFile}) {
      let index = this.files.findIndex(f => f.id === afterFile?.id);
      const toInsert = this.addFileGroupData([file])[0];

      this.files.splice(index + 1, 0, toInsert);
    },

    setFile({file}) {
      this.file = file;
    },

    setFileForSingleFileContext({file}) {
      this.file = file;
      this.files = [file];
    },

    removeFileFromTimelineList({id, contextId, subcontext, collectionDescriptor, fileBeforeUpdate}) {
      if (this.collectionDescriptor !== collectionDescriptor
        || !this.isSameAsLoadedFilesContext({contextType: FILE_CONTEXTS.timeline, contextId, subcontext})) return;

      const fileIndexInStore = this.files?.findIndex(file => String(file.id) === String(id)) ?? -1;
      if (fileIndexInStore < 0) return;
      const removedFile = fileBeforeUpdate || this.files[fileIndexInStore];

      this.excludedTimelineFileIds.add(String(id));
      this.files = this.files.filter(file => String(file.id) !== String(id));
      this.bufferedPages?.forEach(page => {
        page.files = page.files.filter(file => String(file.id) !== String(id));
      });
      this.fileDetails = this.fileDetails?.filter(file => String(file.id) !== String(id)) ?? null;
      // Keep this.file intact until the existing lightbox removal handler navigates.
      useSelection().update({unselect: [removedFile]});
      return {fileIndexInStore};
    },

    async updateFile({id, updates: {name, description, taken_at, taken_at_date_type, rotation, file_url, favorite}}) {
      const originalTimelineCollection = this.collectionDescriptor?.contextType === FILE_CONTEXTS.timeline
        ? this.collectionDescriptor
        : null;
      const isRotating = rotation || rotation === 0;
      const updates = {
        name,
        description,
        taken_at,
        taken_at_date_type,
        file_url,
        favorite
      };

      if (isRotating) {
        updates.rotation = normalizeRotation(rotation);
      }

      const resFile = await useFileApi().updateFile({id, updates});

      if (originalTimelineCollection && originalTimelineCollection !== this.collectionDescriptor) return resFile;

      const updated = this.addFileGroupData([resFile])[0]; //todo: remove uploaded_at

      //if we rotated the file, we need to update the context to reflect the new display
      if (rotation && this.collectionDescriptor?.context?.id && this.collectionDescriptor.contextType) {
        this.updateContext({
          contextId: this.collectionDescriptor.context?.id,
          contextType: this.collectionDescriptor.contextType
        })
      }

      //if rotation was updated we need to assign the UI value used, which may differ from the stored one. This is so the rotation transition plays correctly in the lightbox
      if (isRotating) {
        updated.images.rotation = rotation;
      }

      let detailFile = updated;
      if (this.files) {
        const listFileIndex = this.files.findIndex(file => file.id === updated.id);
        if (listFileIndex > -1) {
          this.files[listFileIndex] = Object.assign(this.files[listFileIndex], updated);
          detailFile = this.files[listFileIndex];
        }
      }
      if (this.file?.id === updated.id) this.setFile({file: detailFile});

      return updated;
    },

    async updateFilesFavorites({files, favorite = true, persistToApi = true}) {
      const ids = files.map(f => f.id);

      if (persistToApi) {
        await useFileApi().updateFiles({ids, updates: {favorite}});
      }

      this.files.forEach(file => {
        if (ids.includes(file.id)) {
          file.favorite = favorite;
        }
      });
    },

    async updateFilesRotation({files}) {
      const fileIdsByRotation = files.reduce((accum, file) => {
        const rotation = normalizeRotation(file.images?.rotation || 0);

        accum[rotation] = accum[rotation] || [];
        accum[rotation].push(file.id);

        return accum;
      }, {});

      await Promise.all(
        [0, 90, 180, 270]
          .filter(rotation => fileIdsByRotation[rotation]?.length)
          .map(rotation => useFileApi().updateFiles({
            ids: fileIdsByRotation[rotation],
            updates: {rotation}
          }))
      );

      const normalizedRotationByFileId = new Map(
        files.map(file => [file.id, normalizeRotation(file.images?.rotation || 0)])
      );

      this.files?.forEach(file => {
        const rotation = normalizedRotationByFileId.get(file.id);

        if (rotation || rotation === 0) {
          file.images = file.images || {};
          file.images.rotation = rotation;
        }
      });

      this.fileDetails?.forEach(file => {
        const rotation = normalizedRotationByFileId.get(file.id);

        if (rotation || rotation === 0) {
          file.images = file.images || {};
          file.images.rotation = rotation;
        }
      });

      if (this.file?.id && normalizedRotationByFileId.has(this.file.id)) {
        this.file.images = this.file.images || {};
        this.file.images.rotation = normalizedRotationByFileId.get(this.file.id);
      }

      if (files.length && this.collectionDescriptor?.context?.id && this.collectionDescriptor.contextType) {
        await this.updateContext({
          contextId: this.collectionDescriptor.context?.id,
          contextType: this.collectionDescriptor.contextType
        });
      }
    },


    async refreshFileDetails({updatedAlbum, updatedUser, updatedTag, updatedPerson}) {
      if (!this.files) {return;}

      let entityId;
      let entityKey;

      if (updatedAlbum) {
        entityId = updatedAlbum.id;
        entityKey = 'albums';
      } else if (updatedUser) {
        entityId = updatedUser.id;
        entityKey = 'user';
      } else if (updatedTag) {
        entityId = updatedTag.id;
        entityKey = 'tags';
      } else if (updatedPerson) {
        entityId = updatedPerson.id;
        entityKey = 'people';
      }

      // Get the ids of the files that have been flagged as detail and contain a certain entity id
      const files = this.files
        .filter(file => {
          if (file.is?.detail) {
            if (entityKey === 'user') {
              return file[entityKey]?.id === entityId;
            } else {
              return file[entityKey]?.some(entity => entity.id === entityId);
            }
          }
        });

      // Fetch and splice the new details
      await this.getFileDetails({files, refresh: true});
    },

    invalidateFileDetails() {
      this.files.forEach(file => {
        if (file.is?.detail) {
          file.is.detail = false;
        }
      });
    },

    async disableShareToken({id}) {
      await useFileApi().disableShareToken({id});

      this.file.share_token = null;
    },

    async resetShareToken({id}) {
      this.file.share_token = await useFileApi().resetShareToken({id});
    },

    addFileGroupData(files) {
      return this.collectionDescriptor?.order?.isGrouped
        ? files.map(file => {

          let dateForGroupName = dayjs(file[this.collectionDescriptor.order.field]);

          if (this.collectionDescriptor.order.adjustToUTC) {
            dateForGroupName = dateForGroupName.utc();
          }

          const groupId = dateForGroupName.format('ll');

          file.group = {
            name: groupId,
            id: groupId,
            fieldVal: file[this.collectionDescriptor.order.field]
          };

          return file;
        })
        : files;
    },


    /**
     * Removes files to the library and updates the library if the current context is the library.
     * @param files - array of file objects
     * @returns {Promise<void>}
     */
    async removeFilesFromLibrary({ids}){
      await useFileApi().deleteFiles({ids});
      this.files = this.files.filter(f => !ids.includes(f.id));
      useSessionStore().getDeleteBinSummary();
      this.getFilesCount();
    },


    /**
     * Adds files to a context and updates the context if the context matches the current context.
     * @param ids - array of file ids
     * @param contextType - album, tag, person
     * @param contextId - id of the context
     * @returns {Promise<void>}
     */
    async addFilesToContext({ids, contextType, contextId}) {
      await useFileApi().addFilesToContext({ids, contextType, contextId});
      this.updateNegativelyFilteredFiles({ids, contextType, contextId});
      await this.updateContext({contextId, contextType});
    },


    /**
     * Removes files from a context and updates the context if the context matches the current context.
     * @param ids - array of file ids
     * @param contextType - album, tag, person
     * @param contextId - id of the context
     * @returns {Promise<void>}
     */
    async removeFilesFromContext({ids, contextType, contextId}) {
      await useFileApi().removeFilesFromContext({ids, contextType, contextId});

      if (contextType === this.collectionDescriptor?.contextType && contextId === this.collectionDescriptor.context?.id) {
        this.files = this.files.filter(f => !ids.includes(f.id));
      }

      await this.updateContext({contextId, contextType});
    },

    /**
     * Moves files from a source context to a destination context and updates both contexts.
     * @param ids - array of file ids
     * @param sourceContextId - id of the source context
     * @param sourceContextType - album or tag
     * @param destinationContextId - id of the destination context
     * @param destinationContextType - album or tag
     * @returns {Promise<void>}
     */
    async moveFiles({ids, sourceContextId, sourceContextType, destinationContextId, destinationContextType}) {
      await useFileApi().moveFiles({ids, sourceContextId, sourceContextType, destinationContextId, destinationContextType});

      if ([sourceContextId, destinationContextType].includes(this.collectionDescriptor.contextType) && [sourceContextId, destinationContextId].includes(this.collectionDescriptor.context?.id)) {
        this.files = this.files.filter(f => !ids.includes(f.id));
      }

      await Promise.all([
        this.updateContext({contextId: sourceContextId, contextType: sourceContextType}),
        this.updateContext({contextId: destinationContextId, contextType: destinationContextType})
      ]);
    },

    /**
     * Checks if the loaded files are filtered by a negative attribute (such as "not in album" or "not in tag")
     * if so, removes the passed in ids if the context checks out
     * @param ids
     * @param contextType
     * @param contextId
     */
    updateNegativelyFilteredFiles({ids, contextType}) {
      const updateForAlbum = this.collectionDescriptor?.organization.includes(FILE_FILTERS.notInAlbum) && contextType === FILE_CONTEXTS.album;
      const updateForTag = this.collectionDescriptor?.organization.includes(FILE_FILTERS.notTagged) && contextType === FILE_CONTEXTS.tag;

      if (updateForAlbum || updateForTag) {
        this.files = this.files.filter(f => !ids.includes(f.id));
      }
    },


    /**
     * Updates the context of the current page if the context matches the current context.
     * I realize this results in multiple calls for the same entity, but it's the simplest
     * way to ensure the context is updated.
     * @param contextId
     * @param contextType
     * @param userId
     * @param shareToken
     * @returns {Promise<void>}
     */
    //todo: can we put the get<Context>One calls in else blocks to avoid duplicate requests?
    async updateContext({contextId, contextType, userId, shareToken}) {
      if (contextType === 'album') {
        if (contextId === useAlbumsStore().album?.id) {
          useAlbumsStore().getAlbum({id: contextId, userId, shareToken});
        }
        useAlbumsStore().getAlbumsOne({id: contextId, userId, shareToken});
      } else if (contextType === 'tag') {
        if (contextId === useTagsStore().tag?.id) {
          useTagsStore().getTag({id: contextId, shareToken});
        }
        useTagsStore().getTagsOne({id: contextId, shareToken});
      } else if (contextType === 'person') {
        if (contextId === usePeopleStore().person?.id) {
          usePeopleStore().getPerson({id: contextId, shareToken});
        }
        usePeopleStore().getPeopleOne({id: contextId, shareToken});
      }

      if (contextType === this.collectionDescriptor?.contextType && contextId === this.collectionDescriptor.context?.id) {
        this.getFilesCount();
      }
    },

    async reorderFiles({album, files, position}) {
      const originalFiles = [...this.files];
      let targetPosition = position;

      //adjust the requested position for the API
      files.forEach(file => {
        const fileIndex = this.files.findIndex(f => file.id === f.id);

        this.files.splice(fileIndex, 1);

        if (fileIndex < position) {
          targetPosition--;
        }
      });

      this.files.splice(targetPosition, 0, ...files);

      try {
        return await useFileApi().reorderFiles(album, files, targetPosition);
      } catch (e) {
        this.files = originalFiles;
        throw e;
      }
    },



    async purgeFiles({ids}) {
      await useFileApi().purgeDeletedFiles({ids});
      this.files = this.files.filter(f => !ids.includes(f.id));
    },

    async restoreFiles({ids}) {
      await useFileApi().restoreFiles({ids});
      this.files = this.files.filter(f => !ids.includes(f.id));
      useSessionStore().getDeleteBinSummary();
    }
  },

  getters: {
    areFiltersApplied: state => Boolean(
      state.collectionDescriptor?.albums?.length ||
      state.collectionDescriptor?.tags?.length ||
      state.collectionDescriptor?.people?.length ||
      state.collectionDescriptor?.fileTypes?.length ||
      state.collectionDescriptor?.organization?.length ||
      state.collectionDescriptor?.dateRange?.val
    ),

    filterCount: state => {
      let count = 0;

      if (state.collectionDescriptor?.albums?.length) {
        count += state.collectionDescriptor.albums.length;
      }

      if (state.collectionDescriptor?.tags?.length) {
        count += state.collectionDescriptor.tags.length;
      }

      if (state.collectionDescriptor?.people?.length) {
        count += state.collectionDescriptor.people.length;
      }

      if (state.collectionDescriptor?.fileTypes?.length) {
        count += state.collectionDescriptor.fileTypes.length;
      }

      if (state.collectionDescriptor?.organization?.length) {
        count += state.collectionDescriptor.organization.length;
      }

      if (state.collectionDescriptor?.dateRange?.val) {
        count++;
      }

      return count;
    },

    filesCountFallback: state => {
      if (state.files?.length < 50) {
        return state.files.length;
      } else {
        return '50+';
      }
    }
  }
});

export const useFilesStore = makeFilesStore();
